import { useEffect } from "react";
import { getModalContentOffset, setModalContentOffset } from "hooks/useDraggableModals";

const MIN_WIDTH = 320;
const MIN_HEIGHT = 240;
const HANDLE_LEFT = "modal-resize-handle--left";
const HANDLE_RIGHT = "modal-resize-handle--right";
const HANDLE_BOTTOM_RIGHT = "modal-resize-handle--bottom-right";
const HOST_CLASS = "modal-resizable-host";
const STYLE_ELEMENT_ID = "resizable-modal-styles-v8";
const HANDLES_VERSION = "7";
const widthByElement = new WeakMap();
const heightByElement = new WeakMap();

const clampWidth = (width) => {
  const max = Math.floor(window.innerWidth * 0.95);
  return Math.min(max, Math.max(MIN_WIDTH, width));
};

const clampHeight = (height) => {
  const max = Math.floor(window.innerHeight * 0.95);
  return Math.min(max, Math.max(MIN_HEIGHT, height));
};

const getElementWidth = (el) => {
  const stored = widthByElement.get(el);
  if (stored) return stored;
  return el.getBoundingClientRect().width;
};

const getElementHeight = (el) => {
  const stored = heightByElement.get(el);
  if (stored) return stored;
  return el.getBoundingClientRect().height;
};

const applyWidth = (el, width, { important = false } = {}) => {
  const next = clampWidth(width);
  widthByElement.set(el, next);
  const value = `${next}px`;
  if (important) {
    el.style.setProperty("width", value, "important");
    el.style.setProperty("max-width", value, "important");
  } else {
    el.style.width = value;
    el.style.maxWidth = value;
  }
};

const applyHeight = (el, height, { important = false } = {}) => {
  const next = clampHeight(height);
  heightByElement.set(el, next);
  const value = `${next}px`;
  if (important) {
    el.style.setProperty("height", value, "important");
    el.style.setProperty("max-height", value, "important");
  } else {
    el.style.height = value;
    el.style.maxHeight = value;
  }
};

const createCornerHandle = () => {
  const corner = document.createElement("div");
  corner.className = `modal-resize-handle ${HANDLE_BOTTOM_RIGHT}`;
  corner.setAttribute("aria-hidden", "true");
  corner.title = "Resize";

  const icon = document.createElement("i");
  icon.className = "mdi mdi-resize-bottom-right modal-resize-grip";
  icon.setAttribute("aria-hidden", "true");
  corner.appendChild(icon);

  return corner;
};

const ensureCursorStyles = () => {
  if (typeof document === "undefined") return;

  const css = `
    .${HOST_CLASS} {
      position: relative;
      overflow: visible !important;
    }
    .modal-resize-handle {
      position: absolute;
      z-index: 30;
      touch-action: none;
    }
    .modal-resize-handle--bottom-right {
      right: 3px;
      bottom: 3px;
      top: auto;
      left: auto;
      width: auto;
      height: auto;
      padding: 0;
      box-sizing: border-box;
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
      color: #64748b;
      pointer-events: none;
      overflow: visible;
      border: none;
      background: transparent;
      transition: color 0.15s ease;
    }
    .modal-resize-handle--bottom-right:has(.modal-resize-grip:hover),
    .modal-resize-handle--bottom-right:has(.modal-resize-grip:active) {
      color: #334155;
    }
    .modal-resize-grip {
      display: block;
      font-size: 18px;
      line-height: 1;
      pointer-events: auto;
      cursor: nwse-resize;
      margin: 0;
      padding: 1px;
      overflow: visible;
      box-sizing: content-box;
      opacity: 0.72;
      transition: opacity 0.15s ease;
    }
    .modal-resize-grip:hover,
    .modal-resize-grip:active {
      opacity: 1;
    }
  `;

  let style = document.getElementById(STYLE_ELEMENT_ID);
  if (!style) {
    document.getElementById("resizable-modal-styles")?.remove();
    document.getElementById("resizable-modal-styles-v2")?.remove();
    document.getElementById("resizable-modal-styles-v3")?.remove();
    document.getElementById("resizable-modal-styles-v4")?.remove();
    document.getElementById("resizable-modal-styles-v5")?.remove();
    style = document.createElement("style");
    style.id = STYLE_ELEMENT_ID;
    document.head.appendChild(style);
  }
  style.textContent = css;
};

const attachHandlesToHost = (host) => {
  if (!host) return;
  if (host.dataset.resizeHandlesAttached === HANDLES_VERSION) return;

  host.querySelectorAll(".modal-resize-handle").forEach((el) => el.remove());
  host.dataset.resizeHandlesAttached = HANDLES_VERSION;
  host.classList.add(HOST_CLASS);
  host.appendChild(createCornerHandle());
};

const attachBootstrapModalHandles = (modalRoot) => {
  const content = modalRoot.querySelector(".modal-content");
  if (!content) return;
  attachHandlesToHost(content);
};

const attachMessageModalHandles = (messageRoot) => {
  const outer = messageRoot.querySelector(".message-modal-outer");
  if (!outer) return;
  attachHandlesToHost(outer);
};

const scanOpenModals = () => {
  document.querySelectorAll(".modal.show").forEach(attachBootstrapModalHandles);
  document.querySelectorAll(".message-modal").forEach(attachMessageModalHandles);
};

const resolveResizeTargets = (handle) => {
  const bootstrapContent = handle.closest(".modal-content");
  if (bootstrapContent) {
    const dialog = handle.closest(".modal-dialog");
    const modalRoot = handle.closest(".modal");
    if (!dialog || !modalRoot?.classList.contains("show")) return null;
    return {
      widthEl: dialog,
      heightEl: bootstrapContent,
      offsetEl: bootstrapContent,
      sizeImportant: false,
    };
  }

  const outer = handle.closest(".message-modal-outer");
  if (outer) {
    const messageRoot = outer.closest(".message-modal");
    const card = outer.querySelector(".message-card");
    if (!messageRoot || !card) return null;
    return {
      widthEl: messageRoot,
      heightEl: card,
      offsetEl: null,
      sizeImportant: true,
    };
  }

  return null;
};

/**
 * Bottom-right resize icon on every reactstrap modal and the call-details conversation panel.
 */
export const useResizableModals = () => {
  useEffect(() => {
    ensureCursorStyles();
    scanOpenModals();

    const observer = new MutationObserver(() => {
      scanOpenModals();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    const resize = {
      active: false,
      edge: null,
      widthEl: null,
      heightEl: null,
      offsetEl: null,
      sizeImportant: false,
      startX: 0,
      startY: 0,
      startWidth: 0,
      startHeight: 0,
      startOffsetX: 0,
      startOffsetY: 0,
    };

    const endResize = () => {
      if (!resize.active) return;
      resize.active = false;
      resize.edge = null;
      resize.widthEl = null;
      resize.heightEl = null;
      resize.offsetEl = null;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    const handleMouseMove = (event) => {
      if (!resize.active || !resize.widthEl) return;

      const deltaX = event.clientX - resize.startX;
      const deltaY = event.clientY - resize.startY;
      const sizeOpts = { important: resize.sizeImportant };

      if (resize.edge === "corner") {
        applyWidth(resize.widthEl, resize.startWidth + deltaX, sizeOpts);
        if (resize.heightEl) {
          applyHeight(resize.heightEl, resize.startHeight + deltaY, sizeOpts);
        }
        return;
      }

      const rawWidth =
        resize.edge === "right"
          ? resize.startWidth + deltaX
          : resize.startWidth - deltaX;
      const clamped = clampWidth(rawWidth);
      const widthDelta = clamped - resize.startWidth;

      applyWidth(resize.widthEl, clamped, sizeOpts);

      if (resize.edge === "left" && resize.offsetEl) {
        setModalContentOffset(resize.offsetEl, {
          x: resize.startOffsetX - widthDelta,
          y: resize.startOffsetY,
        });
      }
    };

    const handleMouseUp = () => {
      endResize();
    };

    const handleMouseDown = (event) => {
      if (event.button !== 0) return;
      const grip = event.target.closest(".modal-resize-grip");
      if (!grip) return;
      const handle = grip.closest(".modal-resize-handle");
      if (!handle) return;

      const targets = resolveResizeTargets(handle);
      if (!targets) return;

      event.preventDefault();
      event.stopPropagation();

      const isCorner = handle.classList.contains(HANDLE_BOTTOM_RIGHT);
      let edge = "corner";
      if (!isCorner) {
        if (handle.classList.contains(HANDLE_LEFT)) edge = "left";
        else if (handle.classList.contains(HANDLE_RIGHT)) edge = "right";
      }

      const offset = targets.offsetEl
        ? getModalContentOffset(targets.offsetEl)
        : { x: 0, y: 0 };

      resize.active = true;
      resize.edge = edge;
      resize.widthEl = targets.widthEl;
      resize.heightEl = targets.heightEl;
      resize.offsetEl = targets.offsetEl;
      resize.sizeImportant = targets.sizeImportant;
      resize.startX = event.clientX;
      resize.startY = event.clientY;
      resize.startWidth = getElementWidth(targets.widthEl);
      resize.startHeight = targets.heightEl
        ? getElementHeight(targets.heightEl)
        : 0;
      resize.startOffsetX = offset.x;
      resize.startOffsetY = offset.y;

      document.body.style.userSelect = "none";
      document.body.style.cursor = isCorner ? "nwse-resize" : "ew-resize";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousedown", handleMouseDown);

    return () => {
      observer.disconnect();
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);
};

export default useResizableModals;
